import { DEFAULT_CONFIDENCE } from '../constants';
import { IntelligentPhotoCaptureArtifactsCategoryNames, IntelligentPhotoCaptureEstimatorContentType, IntelligentPhotoCaptureVinOption, IntelligentPhotoCaptureEstimatorLabelTypeCode } from '../enums';
import { attachmentInfo, identifyAsMovableFolderTypeCodes, getCategoryToFileTypesMapping, getContentTypeToLabelMapping } from '../utilities';
import { isVinLikeContentType, shouldKeepVinAndVinStickerInVehicleInformation } from './intelligentPhotoCaptureEstimatorVinOptionUtilities';
const getVinContentTypesForCopyAs = (vinOption) => {
    if (vinOption === IntelligentPhotoCaptureVinOption.VIN) {
        return [IntelligentPhotoCaptureEstimatorContentType.VIN];
    }
    if (vinOption === IntelligentPhotoCaptureVinOption.VIN_STICKER) {
        return [IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER];
    }
    return [IntelligentPhotoCaptureEstimatorContentType.VIN, IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER];
};
const getCopyAsSupportedContentTypes = (vinOption) => [
    ...getVinContentTypesForCopyAs(vinOption),
    IntelligentPhotoCaptureEstimatorContentType.LICENSEPLATE,
    IntelligentPhotoCaptureEstimatorContentType.ODOMETER,
    IntelligentPhotoCaptureEstimatorContentType.FRONTLEFT,
    IntelligentPhotoCaptureEstimatorContentType.FRONTRIGHT,
    IntelligentPhotoCaptureEstimatorContentType.REARLEFT,
    IntelligentPhotoCaptureEstimatorContentType.REARRIGHT
];
export const copyAsSupportedContentTypes = getCopyAsSupportedContentTypes();
/**
 * Returns the next available temporary attachment id.
 * @param attachmentInfo The current temporary attachment matrix.
 * @returns A unique id greater than every existing attachment id.
 */
/**
 * Returns folder metadata for an attachment.
 * @param attachment The attachment whose folder metadata should be read.
 * @returns The folder metadata collection, or an empty array when none exists.
 */
const getFolderModelInfoCollection = (attachment) => {
    const foldersModelInfo = attachment.foldersModelInfo;
    return Array.isArray(foldersModelInfo) ? foldersModelInfo : [];
};
/**
 * Returns whether a folder type code can be used as a copy-as source.
 * @param folderTypeCode The folder type code to evaluate.
 * @returns True when the folder type belongs to the movable source set.
 */
const isCopyAsMovableFolderType = (folderTypeCode) => identifyAsMovableFolderTypeCodes.some((movableFolderTypeCode) => movableFolderTypeCode === folderTypeCode);
/**
 * Returns whether a folder type code participates in mandatory copy-as coverage.
 * @param folderTypeCode The folder type code to evaluate.
 * @returns True when the folder type is vehicle information or four corners.
 */
const isMandatoryCopyAsFolderType = (folderTypeCode) => folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION ||
    folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.FOUR_CORNERS;
/**
 * Returns whether a folder model contains a supported copy-as content type.
 * @param folderInfo The folder metadata to evaluate.
 * @returns True when the folder metadata has a content type supported by copy-as.
 */
const hasSupportedCopyAsContentType = (folderInfo, vinOption) => !!folderInfo.contentType && getCopyAsSupportedContentTypes(vinOption).includes(folderInfo.contentType);
/**
 * Returns the destination folder type for a copy-as content type.
 * @param contentType The copy-as destination content type.
 * @returns The corresponding folder type, if one exists.
 */
const getFolderTypeCodeForContentType = (contentType) => {
    const categoryToFileTypesMapping = getCategoryToFileTypesMapping();
    if (categoryToFileTypesMapping[IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION].includes(contentType)) {
        return IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION;
    }
    if (categoryToFileTypesMapping[IntelligentPhotoCaptureArtifactsCategoryNames.FOUR_CORNERS].includes(contentType)) {
        return IntelligentPhotoCaptureArtifactsCategoryNames.FOUR_CORNERS;
    }
    return undefined;
};
/**
 * Returns whether any VIN-like content types are present in the vehicle information folder.
 * @param tempAttachmentInfo The current temporary attachment matrix.
 * @returns True when a VIN or VIN sticker is found in the vehicle information folder.
 */
const hasAnyVinLikeInVehicleInformation = (tempAttachmentInfo) => tempAttachmentInfo
    .flat()
    .flatMap((attachment) => getFolderModelInfoCollection(attachment))
    .some((folderInfo) => folderInfo.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
    isVinLikeContentType(folderInfo.contentType));
export var copyAsAttachmentInfo;
(function (copyAsAttachmentInfo) {
    /**
     * Returns the localized label shown for a copy-as destination.
     * @param destinationContentType The destination content type selected for the copy.
     * @param countryCode The country code used to resolve locale-aware labels.
     * @returns The localized label when available, otherwise the raw content type.
     */
    copyAsAttachmentInfo.getCopyAsOptionLabel = (destinationContentType, countryCode) => {
        var _a;
        return (_a = getContentTypeToLabelMapping(countryCode)[destinationContentType]) !== null && _a !== void 0 ? _a : destinationContentType;
    };
    /**
     * Returns the single selected attachment that is eligible for copy-as.
     * @param tempAttachmentInfo The current temporary attachment matrix.
     * @param selectedAttachmentIds The attachment ids selected by the user.
     * @returns The selected attachment when exactly one eligible image is found.
     */
    copyAsAttachmentInfo.getCopyAsEligibleAttachment = (tempAttachmentInfo, selectedAttachmentIds) => {
        const selectedAttachmentIdSet = new Set(selectedAttachmentIds);
        const eligibleSelectedAttachments = tempAttachmentInfo
            .flat()
            .filter((attachment) => selectedAttachmentIdSet.has(attachment.id) &&
            attachmentInfo.isImageMimeType(attachment.mimeType) &&
            getFolderModelInfoCollection(attachment).some((folderInfo) => isCopyAsMovableFolderType(folderInfo.folderTypeCode)));
        return eligibleSelectedAttachments.length === 1 ? eligibleSelectedAttachments[0] : undefined;
    };
    /**
     * Returns mandatory copy-as destinations that are not yet represented in the current attachments.
     * @param tempAttachmentInfo The current temporary attachment matrix.
     * @returns The supported content types that still need a copied attachment.
     */
    copyAsAttachmentInfo.getMissingMandatoryCopyAsContentTypes = (tempAttachmentInfo, vinOption) => {
        const shouldHideVinLikeCopyAsOptions = shouldKeepVinAndVinStickerInVehicleInformation(vinOption) &&
            hasAnyVinLikeInVehicleInformation(tempAttachmentInfo);
        const supportedContentTypes = getCopyAsSupportedContentTypes(vinOption).filter((contentType) => !shouldHideVinLikeCopyAsOptions || !isVinLikeContentType(contentType));
        const existingContentTypes = tempAttachmentInfo
            .flat()
            .flatMap((attachment) => getFolderModelInfoCollection(attachment))
            .reduce((contentTypes, folderInfo) => {
            if (isMandatoryCopyAsFolderType(folderInfo.folderTypeCode) &&
                hasSupportedCopyAsContentType(folderInfo, vinOption)) {
                contentTypes.add(folderInfo.contentType);
            }
            return contentTypes;
        }, new Set());
        return supportedContentTypes.filter((contentType) => !existingContentTypes.has(contentType));
    };
    /**
     * Creates a copied attachment entry for the requested destination content type.
     * @param tempAttachmentInfo The current temporary attachment matrix.
     * @param selectedAttachmentId The selected attachment id.
     * @param destinationContentType The destination content type for the copy.
     * @returns A single-item attachment matrix containing the copied attachment, or an empty array when copying is not allowed.
     */
    copyAsAttachmentInfo.copySelectedAttachmentToContentType = (tempAttachmentInfo, selectedAttachmentId, destinationContentType) => {
        var _a;
        const eligibleAttachment = copyAsAttachmentInfo.getCopyAsEligibleAttachment(tempAttachmentInfo, [selectedAttachmentId]);
        const destinationFolderTypeCode = getFolderTypeCodeForContentType(destinationContentType);
        if (!destinationFolderTypeCode || !eligibleAttachment) {
            return [];
        }
        const copiedFoldersModelInfo = [
            ...((_a = eligibleAttachment.foldersModelInfo) !== null && _a !== void 0 ? _a : []),
            {
                folderTypeCode: destinationFolderTypeCode,
                contentType: destinationContentType,
                labelTypeCode: IntelligentPhotoCaptureEstimatorLabelTypeCode.CLASSIFICATION,
                confidence: DEFAULT_CONFIDENCE
            }
        ];
        const copiedAttachment = Object.assign(Object.assign({}, eligibleAttachment), { foldersModelInfo: copiedFoldersModelInfo });
        return [[copiedAttachment]];
    };
})(copyAsAttachmentInfo || (copyAsAttachmentInfo = {}));
//# sourceMappingURL=intelligentPhotoCaptureCopyAsUtilities.js.map